import { useQuery } from '@tanstack/react-query';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { organizationsApi } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

export interface OrganizationOption {
  id: string;
  name: string;
  nameAr: string;
  type: string;
  vehicleCount?: number;
  activeVehicleCount?: number;
}

/** Roles allowed to look across entities. Everyone else is pinned to theirs. */
const CROSS_ORG_ROLES = new Set(['super_admin', 'ministry_admin']);

export const useCanFilterByOrganization = (): boolean => {
  const { user } = useAuthStore();
  return CROSS_ORG_ROLES.has(user?.role || '');
};

/** Shared list, cached — several pages mount this filter at once. */
export const useOrganizations = () => useQuery({
  queryKey: ['organizations'],
  queryFn: async () => (await organizationsApi.getAll()).data.data as OrganizationOption[],
  staleTime: 10 * 60_000,
});

/**
 * Entity picker used across vehicles, alerts and reports so one filter reads
 * and behaves the same everywhere.
 *
 * Renders nothing for a scoped user: the server already confines them to their
 * own entity, so a control that cannot change anything would only mislead.
 */
export default function OrganizationFilter({
  value, onChange, label = 'الجهة', className = 'input', showCounts = false,
}: {
  value: string;
  onChange: (organizationId: string) => void;
  label?: string;
  className?: string;
  showCounts?: boolean;
}) {
  const canFilter = useCanFilterByOrganization();
  const { data: orgs, isLoading } = useOrganizations();

  if (!canFilter) return null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
        <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        disabled={isLoading}
      >
        <option value="">جميع الجهات</option>
        {(orgs ?? []).map((o) => (
          <option key={o.id} value={o.id}>
            {o.nameAr || o.name}
            {showCounts && o.vehicleCount !== undefined ? ` (${o.vehicleCount})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}
